import {
  type DataGridAnyColDef,
  type DataGridColDef,
  type DataGridColGroupDef,
} from "@vega-core/components/DataGrid/types";
import { isColumnGroup } from "@vega-core/components/DataGrid/utils";

import {
  type ExportColumnConfig,
  type ExportGroupedHeader,
} from "./useTableExport";

/**
 * A display column definition that also knows how to render itself for export.
 * Co-locating the export accessor with the column the grid renders keeps the
 * exported CSV/Excel in lockstep with what's on screen — the export config is
 * derived from these, never kept as a parallel list that silently drifts out of
 * sync (which is how Track Process ended up exporting Allocation's columns).
 */
export type ExportableColDef<TData> = DataGridColDef<TData> & {
  exportValue?: (row: TData) => boolean | number | string | null | undefined;
};

export type ExportableColGroupDef<TData> = Omit<
  DataGridColGroupDef<TData>,
  "children"
> & {
  children: ExportableColDef<TData>[];
};

export type ExportableAnyColDef<TData> =
  | ExportableColDef<TData>
  | ExportableColGroupDef<TData>;

export type DerivedTableExport<TData> = {
  columns: ExportColumnConfig<TData>[];
  groupedHeaders: ExportGroupedHeader[];
};

// Reuses DataGrid's `isColumnGroup` for the runtime check, but re-declares the
// predicate over the Exportable types so narrowing preserves `exportValue` on
// the group's children (the core guard narrows to plain `DataGridColDef`).
const isGroup = <TData>(
  node: ExportableAnyColDef<TData>,
): node is ExportableColGroupDef<TData> => isColumnGroup(node);

const toExportColumn = <TData>({
  headerName,
  exportValue,
}: ExportableColDef<TData>): ExportColumnConfig<TData> | null =>
  exportValue ? { header: headerName ?? "", accessor: exportValue } : null;

const isNotNull = <T>(value: T | null): value is T => value !== null;

/**
 * Builds the {@link useTableExport} config from the column definitions a table
 * renders. Leaves without an `exportValue` (e.g. the row-actions column) are
 * skipped; a column group becomes one grouped header spanning its exportable
 * children. Column order and grouping therefore always mirror the grid.
 */
export const deriveExportColumns = <TData>(
  nodes: ExportableAnyColDef<TData>[],
): DerivedTableExport<TData> => {
  const columns: ExportColumnConfig<TData>[] = [];
  const groupedHeaders: ExportGroupedHeader[] = [];

  nodes.forEach((node) => {
    if (!isGroup(node)) {
      const column = toExportColumn(node);

      if (column) columns.push(column);

      return;
    }

    const groupColumns = node.children.map(toExportColumn).filter(isNotNull);

    if (groupColumns.length === 0) return;

    columns.push(...groupColumns);
    groupedHeaders.push({
      label: node.headerName ?? "",
      colSpan: groupColumns.length,
    });
  });

  return { columns, groupedHeaders };
};

const stripExportValue = <TData>({
  exportValue: _exportValue,
  ...colDef
}: ExportableColDef<TData>): DataGridColDef<TData> => colDef;

/**
 * Returns grid-safe column definitions with `exportValue` removed. AG Grid warns
 * on unknown colDef properties, so the export accessor must never reach it — the
 * same reason the wrapper strips `toggleable`/`aggregateTotal` before render.
 */
export const toGridColumns = <TData>(
  nodes: ExportableAnyColDef<TData>[],
): DataGridAnyColDef<TData>[] =>
  nodes.map((node) =>
    isGroup(node)
      ? { ...node, children: node.children.map(stripExportValue) }
      : stripExportValue(node),
  );
